<template>
  <v-content class="client campaigns page">
    <v-container grid-list-md>
      <v-layout row wrap>
        <v-toolbar flat color="transparent">
          <v-toolbar-title>Campaign Overview</v-toolbar-title>
        </v-toolbar>
        <SignupSourcesCard />
        <ApplicationSourcesCard />
        <v-flex xs12 class="text-xs-right">
          <router-link to="/client/analytics/campaigns">View more analytics</router-link>
        </v-flex>
      </v-layout>
    </v-container>
    <v-container grid-list-xs class="py-3">
      <v-layout row wrap>
        <v-toolbar flat color="transparent">
          <v-toolbar-title>Campaigns</v-toolbar-title>
        </v-toolbar>
        <Campaign
          v-for="campaign in activeCampaigns"
          :key="`campaign-${campaign.slug}`"
          :value="campaign"
          :organization="organization"
        />

        <v-flex xs12>
          <!-- Create campaign form -->
          <v-card v-if="dialogCreate" class="ma-1" style="min-width: 320px;">
            <v-card-title>
              <div class="title">Create campaign</div>
            </v-card-title>
            <v-card-text class="pa-0">
              <v-form @submit.prevent="submit" v-model="valid" class="pa-3">
                <v-text-field
                  label="Slug"
                  v-model="campaign.slug"
                  :rules="rules.slug" required counter="32"
                  :error-messages="campaign.errors.slug || []"
                  :color="themes.client.light.input"
                />
                <v-select
                  label="Type"
                  v-model="campaign.campaignableType"
                  :items="['Organization', 'Opportunity']"
                  required
                  @change="setOrganizationId"
                  :color="themes.client.light.input"
                />
                <v-text-field
                  v-if="campaign.campaignableType === 'Organization'"
                  :placeholder="organization.name"
                  :color="themes.client.light.input"
                  readonly
                />
                <v-select
                  v-if="campaign.campaignableType === 'Opportunity'"
                  label="Opportunity"
                  v-model="campaign.campaignableId"
                  :items="opportunities" item-text="name" item-value="id"
                  required
                  :color="themes.client.light.input"
                />
              </v-form>
            </v-card-text>
            <v-card-actions><v-spacer />
              <v-btn :color="themes.client.light.cancel" flat @click="dialogCreate = false">cancel</v-btn>
              <v-btn :color="themes.client.light.primaryActionButton" flat @click="create" :disabled="!valid">Create</v-btn>
            </v-card-actions>
          </v-card>

          <!-- Create campaign placeholder -->
          <v-card v-else class="create-card ma-1 pb-4" @click.native="dialogCreate = true">
            <v-container fill-height>
              <v-layout column align-center justify-center>
                <v-icon large>mdi-plus</v-icon>
                <div class="title">Create a new campaign</div>
              </v-layout>
            </v-container>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>
    <v-container class="py-3" v-if="hiddenCampaigns.length">
      <v-layout column>
        <v-toolbar flat color="transparent">
          <v-toolbar-title>Hidden Campaigns</v-toolbar-title>
        </v-toolbar>
        <v-card v-for="campaign in hiddenCampaigns" :key="`hidden-campaign-${campaign.slug}`">
          <v-card-title>
            <div>
              <div class="title">{{ campaign.campaignable.name }}</div>
              <span>{{ `${root}/campaign/${campaign.slug}` }}</span>
            </div>
            <v-spacer />
            <v-tooltip left><span>Reactivate</span>
              <v-btn flat icon slot="activator" :color="themes.client.light.muted" @click="reinstate(campaign)">
                <v-icon>mdi-arrow-up-bold-box</v-icon>
              </v-btn>
            </v-tooltip>
          </v-card-title>
        </v-card>
      </v-layout>
    </v-container>
  </v-content>
</template>

<script>
// Mixins
import { managedDocumentAttributes } from '@/mixins/managedDocumentAttributes'

// Components
import Campaign from '@/components/client/campaigns/Campaign'
import ApplicationSourcesCard from '@/components/client/analytics/ApplicationSourcesCard'
import SignupSourcesCard from '@/components/client/analytics/SignupSourcesCard'

// Helpers
import { mapGetters } from 'vuex'

// Vue Component
export default {
  mixins: [managedDocumentAttributes],

  components: {
    Campaign,
    ApplicationSourcesCard,
    SignupSourcesCard,
  },
  created() {
    this.$store.dispatch('client/organization/fetch', this.$route.params.id).then(() => {
      this.documentAttributes.title = `${this.organization.name} Campaigns - Leaply`
    })
    this.$store.dispatch('client/opportunities/fetchAll')
    this.$store.dispatch('client/campaigns/fetchAll')
    this.campaign.campaignableId = this.organization.id
  },
  data() {
    return {
      root: Vue.root,
      dialog: false,
      dialogCreate: false,
      // Model
      campaign: {
        slug: '',
        campaignableId: null,
        campaignableType: 'Organization',
        errors: {},
      },
      // Validations
      valid: false,
      rules: {
        slug: [v => !!v || 'must be provided'],
      },
    }
  },
  computed: {
    ...mapGetters({
      campaigns: 'client/campaigns/all',
      opportunities: 'client/opportunities/all',
      organization: 'client/organization/model',
    }),
    activeCampaigns() { return this.campaigns.filter(c => c.hiddenAt == null) },
    hiddenCampaigns() { return this.campaigns.filter(c => c.hiddenAt != null) },
  },
  methods: {
    setOrganizationId() {
      if (this.campaign.campaignableType === 'Organization') {
        this.campaign.campaignableId = this.organization.id
      }
    },
    create() {
      this.$store
        .dispatch('client/campaigns/create', this.campaign)
        .then(data => {
          if (data.errors && Object.keys(data.errors)[0]) {
            this.$store.dispatch(
              'snackbarError',
              'The campaign could not be created.'
            )
            this.campaign.errors = data.errors
          } else {
            this.$store.dispatch(
              'snackbarSuccess',
              'The campaign was created successfully.'
            )
            this.$store.dispatch('client/campaigns/fetchAll')
            this.dialogCreate = false
            this.campaign = Object.assign(this.campaign, {
              slug: '',
              campaignableId: null,
              campaignableType: 'Organization',
              errors: {},
            })
          }
        })
    },
    reinstate(campaign) {
      this.$store.dispatch('client/campaigns/update', Object.assign(campaign, { hiddenAt: null }))
        .then(
        // Success
        response => {
          this.$store.commit('snackbar', {
            text: 'Campaign reinstated',
            type: 'success',
          })
          this.$store.dispatch('client/campaigns/fetchAll')
        },
        // Failure
        errors => this.$store.commit('snackbar', {
          text: 'There was a problem reinstating the campaign',
          type: 'error',
        })
      )
    },
  },
}
</script>

<style lang="scss">
.client.campaigns.page {
  .create-card {
    min-width: 320px;
    cursor: pointer;

    .title {
      transition: font-size 0.4s;
    }
    &:hover {
      .icon {
        font-size: 42px !important;
      }
      .title {
        font-size: 24px !important;
      }
    }
  }
}
</style>
